import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'

type DragState = {
    element: HTMLDivElement
    pointerID: number
    startClientX: number
    startScrollLeft: number
    hasDragged: boolean
}

const DRAG_THRESHOLD = 4

export const useHorizontalDragScroll = () => {
    const dragState = useRef<DragState | null>(null)
    const shouldSuppressClick = useRef(false)

    const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return

        const element = event.currentTarget
        if (element.scrollWidth <= element.clientWidth) return

        shouldSuppressClick.current = false
        dragState.current = {
            element,
            pointerID: event.pointerId,
            startClientX: event.clientX,
            startScrollLeft: element.scrollLeft,
            hasDragged: false,
        }
        element.dataset.dragging = 'true'
        element.setPointerCapture(event.pointerId)
    }

    const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const state = dragState.current
        if (!state || state.pointerID !== event.pointerId) return

        const deltaX = event.clientX - state.startClientX
        if (!state.hasDragged && Math.abs(deltaX) < DRAG_THRESHOLD) return

        state.hasDragged = true
        state.element.scrollLeft = state.startScrollLeft - deltaX
        event.preventDefault()
    }

    const handlePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
        const state = dragState.current
        if (!state || state.pointerID !== event.pointerId) return

        state.element.removeAttribute('data-dragging')
        shouldSuppressClick.current = state.hasDragged
        dragState.current = null
    }

    const handleClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!shouldSuppressClick.current) return

        shouldSuppressClick.current = false
        event.preventDefault()
        event.stopPropagation()
    }

    return {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerEnd,
        onPointerCancel: handlePointerEnd,
        onLostPointerCapture: handlePointerEnd,
        onClickCapture: handleClickCapture,
    }
}